import { ref, onMounted, onUnmounted, watch } from "vue";
import type { Ref } from "vue";
import { useDocumentObserver } from "./useDocumentObserver";
import { useWindowScroll } from "@vueuse/core";

interface Bounds {
    left: number;
    top: number;
    right: number;
    bottom: number;
    width: number;
    height: number;
}

interface ElementBounds {
    bounds: Ref<Bounds | null>;
}

export function useEditableElementBounds(elementRef: Ref<HTMLElement | null>): ElementBounds {
    const bounds = ref<Bounds | null>(null);
    const { subscribe, unsubscribe } = useDocumentObserver();
    const { x: scrollX, y: scrollY } = useWindowScroll();

    function calculateBounds(): void {
        if (!elementRef.value) {
            bounds.value = null;
            return;
        }

        const children = Array.from(elementRef.value.children) as HTMLElement[];
        if (children.length === 0) {
            bounds.value = null;
            return;
        }

        if (children.length === 1) {
            bounds.value = children[0].getBoundingClientRect();
            return;
        }

        // For multiple children, calculate combined bounds
        bounds.value = children.reduce<Bounds | null>((bounds, child) => {
            const childRect = child.getBoundingClientRect();
            if (!bounds) return childRect;

            return {
                left: Math.min(bounds.left, childRect.left),
                top: Math.min(bounds.top, childRect.top),
                right: Math.max(bounds.right, childRect.right),
                bottom: Math.max(bounds.bottom, childRect.bottom),
                width:
                    Math.max(bounds.right, childRect.right) - Math.min(bounds.left, childRect.left),
                height:
                    Math.max(bounds.bottom, childRect.bottom) - Math.min(bounds.top, childRect.top),
            };
        }, null);
    }

    onMounted(() => {
        if (!elementRef.value) return;

        calculateBounds();
        subscribe(calculateBounds);
    });

    onUnmounted(() => {
        if (!elementRef.value) return;

        unsubscribe(calculateBounds);
    });

    watch([scrollX, scrollY], calculateBounds);

    return {
        bounds,
    };
}
